import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Notification {
  id: string;
  title: string;
  content: string | null;
  type: string;
  is_read: boolean;
  created_at: string;
}

const notifRoute = (type: string) => {
  if (type === 'support_request' || type === 'support_reply') return '/quan-tri/tin-nhan-ho-tro';
  return '/quan-tri/nap-rut';
};

const notifIcon = (type: string) => {
  if (type === 'support_request' || type === 'support_reply') return 'ri-customer-service-2-line';
  return 'ri-bank-card-line';
};

export default function AdminNotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const unread = notifications.filter((n) => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) return;
      const { data: notifRaw } = await supabase.rpc('get_user_notifications_json', { p_user_id: userId });
      const notifArr = Array.isArray(notifRaw) ? notifRaw : JSON.parse((notifRaw as string) || '[]');
      setNotifications(notifArr.slice(0, 20) || []);
    } catch (err) {
      console.error('fetchNotifications error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchNotifications();
  }, [open, fetchNotifications]);

  const markAllRead = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) return;
      const { error, count } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false)
        .select();
      if (error) throw error;
      if (count === 0) {
        await supabase.from('notifications').update({ is_read: true }).eq('id', notifications[0]?.id);
      }
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      await fetchNotifications();
    } catch (err) {
      console.error('markAllRead error:', err);
    }
  };

  const markRead = async (id: string) => {
    setOpen(false);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    try {
      const { error } = await supabase.from('notifications').update({ is_read: true }).eq('id', id);
      if (error) throw error;
      await fetchNotifications();
    } catch (err) {
      console.error('markRead error:', err);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-full flex items-center justify-center text-foreground-600 hover:text-foreground-900 hover:bg-background-200/60 cursor-pointer transition-colors"
        aria-label="Thông báo"
      >
        <i className="ri-notification-3-line text-lg"></i>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-background-950 text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-background-100 border border-background-200/60 rounded-xl overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/60">
            <span className="text-sm font-semibold text-foreground-950">Thông báo</span>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs font-semibold text-primary-400 hover:text-primary-300 cursor-pointer whitespace-nowrap"
              >
                Đánh dấu đã đọc
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <p className="px-4 py-6 text-center text-sm text-foreground-600">Đang tải...</p>
            ) : notifications.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-foreground-600">Chưa có thông báo</p>
            ) : (
              notifications.map((n) => (
                <Link
                  key={n.id}
                  to={notifRoute(n.type)}
                  onClick={() => markRead(n.id)}
                  className={`block px-4 py-3 border-b border-background-200/40 hover:bg-background-200/40 cursor-pointer transition-colors ${n.is_read ? '' : 'bg-primary-500/5'}`}
                >
                  <div className="flex items-start gap-2">
                    <i className={`${notifIcon(n.type)} text-accent-400 text-base mt-0.5`}></i>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground-950 leading-snug">{n.title}</p>
                      <p className="text-xs text-foreground-600 mt-0.5 line-clamp-2">{n.content}</p>
                      <p className="text-[10px] text-foreground-500 mt-1">
                        {new Date(n.created_at).toLocaleString('vi-VN')}
                      </p>
                    </div>
                    {!n.is_read && <span className="w-2 h-2 rounded-full bg-primary-400 mt-1 shrink-0"></span>}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}